import { useEffect } from 'react';
import confetti from 'canvas-confetti';
import { Card, CardContent } from '@/components/ui/card';
import { Trophy, Target, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SuccessCelebrationProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'level_up' | 'goal_complete';
  title: string;
  description?: string;
  level?: number;
  className?: string;
}

export function SuccessCelebration({
  isOpen,
  onClose,
  type,
  title,
  description,
  level,
  className
}: SuccessCelebrationProps) {
  
  useEffect(() => {
    if (isOpen) {
      // Trigger confetti
      const duration = 3000;
      const animationEnd = Date.now() + duration;
      
      const randomInRange = (min: number, max: number) => {
        return Math.random() * (max - min) + min;
      };

      const colors = type === 'level_up' 
        ? ['#FFD400', '#6B8E23', '#8E3B5A'] // Level up: primary palette
        : ['#FFD400', '#D76F3E', '#98B06F']; // Goal: achievement colors

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          clearInterval(interval);
          return;
        }

        const particleCount = 50;

        confetti({
          particleCount,
          startVelocity: 30,
          spread: 360,
          origin: {
            x: randomInRange(0.1, 0.9),
            y: Math.random() - 0.2
          },
          colors,
          ticks: 200,
          gravity: 1,
          decay: 0.94,
          scalar: randomInRange(0.8, 1.2),
        });
      }, 250);

      // Close after animation
      const timeout = setTimeout(() => {
        onClose();
      }, duration + 500);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }
  }, [isOpen, type, onClose]);

  if (!isOpen) return null;

  const Icon = type === 'level_up' ? Trophy : Target;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm animate-fade-in">
      <Card className={cn("card-glow max-w-md animate-scale-in", className)}>
        <CardContent className="p-8 text-center space-y-4">
          {/* Animated Icon */}
          <div className="relative mx-auto w-fit">
            <div className="absolute inset-0 rounded-full bg-primary/30 blur-3xl animate-pulse" />
            <div className="relative rounded-full bg-gradient-to-br from-primary via-secondary to-accent p-8 animate-bounce-subtle">
              <Icon className="w-16 h-16 text-white" strokeWidth={2} />
            </div>
            <Sparkles className="absolute -top-2 -right-2 w-8 h-8 text-primary animate-pulse" />
          </div>

          {/* Title */}
          <div>
            <h2 className="text-3xl font-heading font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent mb-2">
              {title}
            </h2>
            {level !== undefined && (
              <p className="text-5xl font-heading font-bold text-primary animate-bounce-subtle">
                Level {level}
              </p>
            )}
          </div>

          {/* Description */}
          {description && (
            <p className="text-muted-foreground text-lg">
              {description}
            </p>
          )}

          {/* Sparkles decoration */}
          <div className="flex justify-center gap-4 text-primary/50">
            <Sparkles className="w-4 h-4 animate-pulse" style={{ animationDelay: '0s' }} />
            <Sparkles className="w-5 h-5 animate-pulse" style={{ animationDelay: '0.2s' }} />
            <Sparkles className="w-4 h-4 animate-pulse" style={{ animationDelay: '0.4s' }} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
